import { Box, Package, Plus, Server, X } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import client from "../api/client";

export default function DefaultItemsEditor({
	items = [],
	onChange,
	companyId,
}) {
	const [plans, setPlans] = useState([]);
	const [modules, setModules] = useState([]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const [plansRes, modulesRes] = await Promise.all([
					client.get("/plans", { params: { limit: 500 } }),
					client.get("/modules", { params: { limit: 500 } }),
				]);
				setPlans(plansRes.data.data || plansRes.data || []);
				setModules(modulesRes.data.data || modulesRes.data || []);
			} catch (e) {
				console.error("Error loading plans/modules:", e);
			}
		};
		fetchData();
	}, []);

	const filteredPlans = companyId
		? plans.filter((p) => String(p.company_id) === String(companyId))
		: plans;

	const handleAdd = (type, id) => {
		if (!id) return;
		const source = type === "plan" ? plans : modules;
		const found = source.find((x) => x.id === parseInt(id, 10));
		if (!found) return;
		onChange([
			...items,
			{
				item_type: type,
				item_id: found.id,
				name: found.name,
				value: found.value,
				quantity: 1,
			},
		]);
	};

	const handleRemove = (idx) => {
		onChange(items.filter((_, i) => i !== idx));
	};

	const handleQuantityChange = (idx, qty) => {
		const updated = [...items];
		updated[idx] = {
			...updated[idx],
			quantity: Math.max(1, parseInt(qty, 10) || 1),
		};
		onChange(updated);
	};

	const total = items.reduce(
		(sum, it) => sum + parseFloat(it.value || 0) * (it.quantity || 1),
		0,
	);

	return (
		<div className="border-t border-slate-700 pt-4">
			<div className="flex items-center justify-between mb-2">
				<h3 className="text-sm font-semibold text-white flex items-center gap-2">
					<Package className="w-4 h-4 text-emerald-400" />
					Itens Padrao ({items.length})
				</h3>
			</div>
			<p className="text-[10px] text-slate-600 mb-3">
				Planos e modulos adicionados automaticamente ao usar este template.
			</p>

			<div className="grid grid-cols-2 gap-2 mb-3">
				<div>
					<label
						htmlFor="add-plan"
						className="block text-[10px] text-slate-500 mb-1"
					>
						<Plus className="w-3 h-3 inline mr-0.5" />
						Adicionar Plano
					</label>
					<select
						id="add-plan"
						value=""
						onChange={(e) => handleAdd("plan", e.target.value)}
						className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-cyan-500/50"
					>
						<option value="">Selecionar plano...</option>
						{filteredPlans.map((p) => (
							<option key={p.id} value={p.id}>
								{p.name} - R$ {parseFloat(p.value).toFixed(2)}
							</option>
						))}
					</select>
				</div>
				<div>
					<label
						htmlFor="add-module"
						className="block text-[10px] text-slate-500 mb-1"
					>
						<Plus className="w-3 h-3 inline mr-0.5" />
						Adicionar Modulo
					</label>
					<select
						id="add-module"
						value=""
						onChange={(e) => handleAdd("module", e.target.value)}
						className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-cyan-500/50"
					>
						<option value="">Selecionar modulo...</option>
						{modules.map((m) => (
							<option key={m.id} value={m.id}>
								{m.name} - R$ {parseFloat(m.value).toFixed(2)}
							</option>
						))}
					</select>
				</div>
			</div>

			{items.length > 0 && (
				<div className="space-y-1">
					{items.map((item, idx) => (
						<div
							key={`item-${idx}`}
							className="flex items-center gap-2 p-2 bg-slate-900/50 rounded border border-slate-700/30"
						>
							<div
								className={`w-6 h-6 rounded flex items-center justify-center ${
									item.item_type === "plan"
										? "bg-cyan-500/20"
										: "bg-purple-500/20"
								}`}
							>
								{item.item_type === "plan" ? (
									<Server className="w-3 h-3 text-cyan-400" />
								) : (
									<Box className="w-3 h-3 text-purple-400" />
								)}
							</div>
							<span className="text-xs text-white flex-1 truncate">
								{item.name}
							</span>
							<input
								type="number"
								min="1"
								value={item.quantity}
								onChange={(e) => handleQuantityChange(idx, e.target.value)}
								className="w-12 bg-slate-700/50 border border-slate-600 rounded px-1 py-0.5 text-white text-center text-[10px]"
							/>
							<span className="text-[10px] text-green-400 font-bold w-20 text-right">
								R${" "}
								{(parseFloat(item.value || 0) * (item.quantity || 1)).toFixed(
									2,
								)}
							</span>
							<button
								type="button"
								onClick={() => handleRemove(idx)}
								className="text-slate-500 hover:text-red-400 p-0.5"
							>
								<X className="w-3 h-3" />
							</button>
						</div>
					))}

					<div className="flex justify-between items-center pt-2 border-t border-slate-700/30">
						<span className="text-[10px] text-slate-500 uppercase font-bold">
							Total
						</span>
						<span className="text-sm text-white font-bold">
							R$ {total.toFixed(2)}
						</span>
					</div>
				</div>
			)}

			{items.length === 0 && (
				<div className="text-center py-6 border border-dashed border-slate-700/50 rounded-lg">
					<Package className="w-6 h-6 mx-auto text-slate-600 mb-1" />
					<p className="text-[10px] text-slate-600">
						Nenhum item padrao adicionado
					</p>
				</div>
			)}
		</div>
	);
}

DefaultItemsEditor.propTypes = {
	items: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
